import { IContact } from '@dataTypes/contact.type'
import { NoteContextType } from '@dataTypes/note.type'
import {
  updateSelectedMessage,
  createConversation,
  unblockUser,
  fetchAllConversations,
  setContactNotes,
} from '@store/actions'
import { fetchAllNotes } from '@store/actions/note.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useGlobalLoadingActions } from '@hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { ChatChannel, IPatient } from '@dataTypes/chat.type'
import { IBlockedUser } from '@dataTypes/blocked-user.type'
import { MenuEventArgs } from '@syncfusion/ej2-react-splitbuttons'
import { getFullName } from '@utils/fullname.util'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  SET_ACTIVE_TAB,
  SET_CONTACT,
  SET_PATIENT,
  SET_SHOW_MODAL,
} from '@store/reducers/messageCenter.reducer'
import type { NotesLocationState } from '@components/common/NoteEditorOverlay/notesFeature.types'
import type { Patient } from '@dataTypes/PatientInfo.types'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { getCityList, getStateList } from '@utils/address.util'
import {
  setContactFilters,
  setContactSearchQuery,
} from '@store/reducers/contact.reducer'

const MESSAGE_CENTER_LOADING_CONTAINER = '.message-container-page'

const digitsOnly = (value: string): string => value.replace(/\D/g, '')

const phoneMatchesQuery = (
  phone: string | undefined,
  query: string,
  queryDigits: string,
): boolean => {
  if (!phone) return false
  if (phone.toLowerCase().includes(query)) return true
  return queryDigits.length > 0 && digitsOnly(phone).includes(queryDigits)
}

interface ContactHooks {
  blockedUsers: IBlockedUser[]
  contactsState: IContact[]
  filters: FilterSelection
  handleAddContact: () => void
  handleCloseAddContactModal: () => void
  handleCloseSelectContactModal: () => void
  handleDropdownAction: (args: MenuEventArgs, contact: IContact) => void
  handleFilterChange: (selectedFilters: FilterSelection) => void
  handlePhoneAction: (contact: IContact) => void
  handleSearch: (event: ChangeEvent<HTMLInputElement>) => void
  handleSendMessage: (contact: IContact) => void
  handleShowManageGroupModal: () => void
  onClickDropdownAction: (contact: IContact) => void
  onHideConfirmBlockModal: () => void
  onShowNotes: (contact: IContact) => void
  onUnblockUser: () => void
  filteredContacts: IContact[] | []
  filterSelectionList: FilterSelection
  searchQuery: string
  selectedContact: IContact | null
  showAddContactModal: boolean
  showConfirmUnblockModal: boolean
  showSelectContactModal: boolean
  contactsLoading: boolean
}

type UseContactProps = {
  onOpenNotesOverlay?: (
    state: NotesLocationState,
    patient: Patient | null,
  ) => void
}

const useContact = ({
  onOpenNotesOverlay,
}: UseContactProps = {}): ContactHooks => {
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const blockedUsers = useAppSelector((state) => state.blockedUser.data)
  const conversations = useAppSelector((state) => state.chat.data)
  const contactsState = useAppSelector((state) => state.contacts.data ?? [])
  const contactsLoading = useAppSelector((state) => state.contacts.loading)
  const { searchQuery, filters } = useAppSelector((state) => state.contacts)

  const [selectedContact, setSelectedContact] = useState<IContact | null>(null)
  const [filteredContacts, setFilteredContacts] =
    useState<IContact[]>(contactsState)
  const [showAddContactModal, setShowAddContactModal] = useState<boolean>(false)
  const [showSelectContactModal, setShowSelectContactModal] =
    useState<boolean>(false)
  const [showConfirmUnblockModal, setShowConfirmUnblockModal] =
    useState<boolean>(false)

  const handleCloseAddContactModal = (): void => {
    setShowAddContactModal(false)
    setSelectedContact(null)
  }

  const handleCloseSelectContactModal = (): void => {
    setShowSelectContactModal(false)
    setSelectedContact(null)
  }

  const onUnblockUser = async (): Promise<void> => {
    if (!selectedContact) return
    const blockedUserId = blockedUsers.find(
      (blockedUser) => blockedUser.contact_id === selectedContact.contact_id,
    )?.blocked_user_id
    if (!blockedUserId) return

    try {
      await dispatch(unblockUser(blockedUserId)).unwrap()
      showToast(
        createSuccessToast(
          'User unblocked',
          'The contact has been unblocked and can message you again.',
        ),
      )
      setShowConfirmUnblockModal(false)
      setSelectedContact(null)
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to unblock user. Please try again.'
      showToast(createFailedToast('Failed to unblock user', message))
    }
  }

  useEffect(() => {
    if (!contactsLoading && contactsState.length > 0) {
      let _contacts = contactsState
      _contacts = _contacts.filter((contact) => {
        if (filters.city.length !== 0) {
          if (contact.city) return filters.city.includes(contact.city)
          else return false
        } else return true
      })

      _contacts = _contacts.filter((contact) => {
        if (filters.state.length !== 0) {
          if (contact.state) return filters.state.includes(contact.state)
          else return false
        } else return true
      })

      _contacts = _contacts.filter((contact) => {
        if (searchQuery.length !== 0) {
          const query = searchQuery.toLowerCase()
          const queryDigits = digitsOnly(searchQuery)
          return (
            contact.first_name.toLowerCase().includes(query) ||
            contact.last_name.toLowerCase().includes(query) ||
            contact.email?.toLowerCase().includes(query) ||
            phoneMatchesQuery(contact.cellphone, query, queryDigits) ||
            phoneMatchesQuery(contact.home_phone, query, queryDigits) ||
            phoneMatchesQuery(contact.work_phone, query, queryDigits) ||
            phoneMatchesQuery(contact.fax_number, query, queryDigits) ||
            contact.contact_id?.toLowerCase().includes(query)
          )
        } else return true
      })
      setFilteredContacts(_contacts)
    }
  }, [searchQuery, filters, contactsLoading, contactsState])

  // Populate contact_note for each contact so the Notes column can show "View" when a contact has notes. Single API call: get all notes by context_type=contact, then group by contact_id.
  const hasFetchedContactNotesRef = useRef(false)
  const contactsListLengthRef = useRef(0)
  useEffect(() => {
    if (contactsLoading || contactsState.length === 0) return
    if (contactsState.length !== contactsListLengthRef.current) {
      contactsListLengthRef.current = contactsState.length
      hasFetchedContactNotesRef.current = false
    }
    if (hasFetchedContactNotesRef.current) return
    const needsNotes = contactsState.some(
      (c) => c.contact_id && c.contact_note === undefined,
    )
    if (!needsNotes) return
    hasFetchedContactNotesRef.current = true
    dispatch(
      fetchAllNotes({
        patientId: undefined,
        contactId: undefined,
        contextType: NoteContextType.CONTACT,
        contextId: undefined,
      }),
    )
      .unwrap()
      .then((allNotes) => {
        const notesList = allNotes ?? []
        const byContactId = new Map<string, typeof notesList>()
        for (const note of notesList) {
          const cid = note?.contact_id
          if (cid) {
            const arr = byContactId.get(cid) ?? []
            arr.push(note)
            byContactId.set(cid, arr)
          }
        }
        contactsState.forEach((c) => {
          if (c.contact_id) {
            dispatch(
              setContactNotes({
                contactId: c.contact_id,
                notes: byContactId.get(c.contact_id) ?? [],
              }),
            )
          }
        })
      })
      .catch(() => {
        hasFetchedContactNotesRef.current = false
        contactsState.forEach((c) => {
          if (c.contact_id) {
            dispatch(setContactNotes({ contactId: c.contact_id, notes: [] }))
          }
        })
      })
  }, [contactsLoading, contactsState, dispatch])

  const getSerializableContact = (contact: IContact): IContact => {
    if (!contact.contact_id) return contact
    return (
      contactsState.find((item) => item.contact_id === contact.contact_id) ??
      contact
    )
  }

  const onShowNotes = (contact: IContact): void => {
    const serializableContact = getSerializableContact(contact)
    setSelectedContact(serializableContact)
    dispatch(SET_CONTACT(serializableContact))
    if (serializableContact.patient) {
      dispatch(SET_PATIENT(serializableContact.patient as IPatient))
    }
    onOpenNotesOverlay?.(
      {
        patientId:
          serializableContact.patient?.patient_id ??
          serializableContact.patient_id,
        contactId: serializableContact.contact_id,
        contextId: serializableContact.contact_id,
        previousPage: {
          path: '/message',
          tab: 'contact' as const,
        },
        contextName: getFullName(serializableContact, true) ?? 'Contact',
        contextType: NoteContextType.CONTACT,
        openFirstNote: true,
      },
      (serializableContact.patient as unknown as Patient | undefined) ?? null,
    )
  }

  const handleAddContact = (): void => {
    setSelectedContact(null)
    setShowAddContactModal(true)
  }

  const onHideConfirmBlockModal = (): void => {
    setShowConfirmUnblockModal(false)
    setSelectedContact(null)
  }

  const handleSendMessage = async (contact: IContact): Promise<void> => {
    dispatch(SET_ACTIVE_TAB('chat'))

    // Find existing SMS conversation by phone number or contact_id
    const chat = conversations?.find((c) => {
      // Only check SMS conversations
      if (c.channel !== 'SMS' || c.is_hidden) {
        return false
      }

      // Only check conversations with a single participant
      if (!c.chat_participant || c.chat_participant.length !== 1) {
        return false
      }

      const participant = c.chat_participant[0]

      // Check#1: match by phone number
      const phoneMatch = participant.phone_number === contact.cellphone

      // Check#2: match by contact_id if available
      const contactIdMatch =
        contact.contact_id &&
        participant.contact_id &&
        participant.contact_id === contact.contact_id

      return phoneMatch || contactIdMatch
    })

    if (chat && chat.chat_conversation_id) {
      dispatch(updateSelectedMessage({ id: chat.chat_conversation_id }))
      return
    }

    try {
      showLoading({ container: MESSAGE_CENTER_LOADING_CONTAINER })
      await dispatch(
        createConversation({
          conversation_data: {
            channel: ChatChannel.sms,
            chat_message: [],
            chat_participant: [],
          },
          members_data: [
            {
              chat_conversation_id: '',
              phone_number: contact.cellphone,
              contact_id: contact.contact_id ?? undefined,
            },
          ],
        }),
      ).unwrap()
      await dispatch(fetchAllConversations()).unwrap()
      showToast(
        createSuccessToast(
          'Conversation started',
          'A new chat has been created. You can start messaging.',
        ),
      )
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to start conversation. Please try again.'
      showToast(createFailedToast('Failed to start conversation', message))
    } finally {
      hideLoading(MESSAGE_CENTER_LOADING_CONTAINER)
    }
  }

  const handlePhoneAction = (contact: IContact): void => {
    setSelectedContact(contact)
    setShowSelectContactModal(true)
  }

  const onClickDropdownAction = (contact: IContact): void => {
    const serializableContact = getSerializableContact(contact)
    setSelectedContact(serializableContact)
    dispatch(SET_CONTACT(serializableContact))
    if (serializableContact.patient) {
      dispatch(SET_PATIENT(serializableContact.patient as IPatient))
    }
  }

  const handleDropdownAction = (
    args: MenuEventArgs,
    contact: IContact,
  ): void => {
    const serializableContact = getSerializableContact(contact)
    const action = args.item.id
    if (action === 'edit') {
      setSelectedContact(serializableContact)
      setShowAddContactModal(true)
    } else if (action === 'set-appointment') {
      dispatch(SET_SHOW_MODAL({ appointment: true }))
    } else if (action === 'add-note') {
      dispatch(SET_CONTACT(serializableContact))
      if (serializableContact.patient) {
        dispatch(SET_PATIENT(serializableContact.patient as IPatient))
      }
      onOpenNotesOverlay?.(
        {
          patientId:
            serializableContact.patient?.patient_id ??
            serializableContact.patient_id,
          contactId: serializableContact.contact_id,
          contextId: serializableContact.contact_id,
          previousPage: {
            path: '/message',
            tab: 'contact' as const,
          },
          contextName: getFullName(serializableContact, true) ?? 'Contact',
          contextType: NoteContextType.CONTACT,
        },
        (serializableContact.patient as unknown as Patient | undefined) ?? null,
      )
    } else if (action === 'delete') {
      setSelectedContact(serializableContact)
      dispatch(SET_CONTACT(serializableContact))
      dispatch(SET_SHOW_MODAL({ delete: true }))
    } else if (action === 'unblock') {
      setSelectedContact(serializableContact)
      setShowConfirmUnblockModal(true)
    }
  }

  const filterSelectionList = useMemo(
    () => ({
      city: getCityList(contactsState),
      state: getStateList(contactsState),
    }),
    [contactsState],
  )

  const handleSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    dispatch(setContactSearchQuery(event.target.value))
  }

  const handleFilterChange = (selectedFilters: FilterSelection): void => {
    dispatch(setContactFilters({ ...filters, ...selectedFilters }))
  }

  const handleShowManageGroupModal = (): void => {
    dispatch(SET_SHOW_MODAL({ manageGroup: true }))
  }

  return {
    blockedUsers,
    contactsState,
    filters,
    filterSelectionList,
    filteredContacts,
    handleAddContact,
    handleCloseAddContactModal,
    handleCloseSelectContactModal,
    handleDropdownAction,
    handleFilterChange,
    handlePhoneAction,
    handleSearch,
    handleShowManageGroupModal,
    handleSendMessage,
    onClickDropdownAction,
    onHideConfirmBlockModal,
    onShowNotes,
    onUnblockUser,
    searchQuery,
    selectedContact,
    showAddContactModal,
    showConfirmUnblockModal,
    showSelectContactModal,
    contactsLoading,
  }
}

export default useContact
